/**
 * An affiliate's codes (TMMPDM-0042). Read-only: the link can be copied, never edited.
 * `label` absent renders "—".
 */
import { useEffect, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { EmptyState, ErrorState, useNotify } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { stickyTableContainerSx } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import type { AffiliateCodeStatus, AffiliateCodeView } from '@/api/tmm/types';
import { StatusChip } from '@/status';

const STATUS_FALLBACK: Record<AffiliateCodeStatus, string> = {
  ACTIVE: 'Active',
  PAUSED: 'Paused',
};

interface AffiliateCodesSectionProps {
  slug: string;
  affiliateId: number;
}

export function AffiliateCodesSection({ slug, affiliateId }: AffiliateCodesSectionProps) {
  const { t } = useCopy();
  const notify = useNotify();
  const [reloadToken, setReloadToken] = useState(0);
  const [codes, setCodes] = useState<AffiliateCodeView[] | null>(null);
  const [error, setError] = useState<{ cause: unknown } | null>(null);
  const absent = t('affiliates.absent', '—');

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listAffiliateCodes(slug, affiliateId)
      .then((list) => {
        if (cancelled) return;
        setCodes(list.codes);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setCodes(null);
          setError({ cause: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, affiliateId, reloadToken]);

  const copy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      notify.success(t('components.copied', 'Copied'));
    } catch {
      notify.info(t('components.copyFailed', "Couldn't copy — select the text instead."));
    }
  };

  if (error) {
    return (
      <ErrorState
        error={error.cause}
        message={t('affiliates.codes.loadError', 'Could not load codes.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }
  if (codes === null) return <LoadingSpinner />;
  if (codes.length === 0) {
    return <EmptyState surface title={t('affiliates.codes.empty', 'No codes yet.')} />;
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={stickyTableContainerSx}>
      <Table size="small" stickyHeader aria-label={t('affiliates.codes.aria', 'Codes')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('affiliates.col.code', 'Code')}</TableCell>
            <TableCell>{t('affiliates.col.label', 'Label')}</TableCell>
            <TableCell>{t('affiliates.col.status', 'Status')}</TableCell>
            <TableCell>{t('affiliates.col.link', 'Link')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {codes.map((code) => {
            const copyLabel = t('affiliates.codes.copy', 'Copy link for {{code}}', {
              code: code.code,
            });
            return (
              <TableRow key={code.id} hover>
                <TableCell>{code.code}</TableCell>
                <TableCell>{code.label ?? absent}</TableCell>
                <TableCell>
                  <StatusChip
                    status={code.status}
                    label={t(`affiliates.codeStatus.${code.status}`, STATUS_FALLBACK[code.status])}
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ mr: 0.5, overflowWrap: 'anywhere' }}
                  >
                    {code.link}
                  </Typography>
                  <Tooltip title={copyLabel}>
                    <IconButton
                      size="small"
                      aria-label={copyLabel}
                      onClick={() => void copy(code.link)}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
